import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { fmtEur } from '../../utils/formatArcaNumbers';
import { projectComparisonByYear } from '../../utils/monthlyProjection';
import { PlanReturnView, periodRangeLabel, rateOf, ageNote } from '../../utils/transferReturns';
import { SeriesLabel, SeriesName, legendStatsShort, periodWithRange } from './projectionLegend';

interface CustomTooltipProps {
	active?: boolean;
	payload?: { value: number; dataKey: string }[];
	label?: number;
	planName: SeriesName;
	savingName: SeriesName;
}

/** Como en la gráfica de traspaso: el nombre se puede recortar, el importe nunca. */
const CustomTooltip: React.FC<CustomTooltipProps> = ({
	active,
	payload,
	label,
	planName,
	savingName
}) => {
	if (!active || !payload?.length) return null;
	const plan = payload.find((p) => p.dataKey === 'plan');
	const saving = payload.find((p) => p.dataKey === 'bank');
	return (
		<div className="bg-popover border border-border rounded-lg shadow-md px-3 py-2 text-xs space-y-1 min-w-0 max-w-[320px]">
			<p className="font-semibold text-foreground">{label === 0 ? 'Hoy' : `Año ${label}`}</p>
			{plan && (
				<p data-series="plan" className="flex items-center gap-1.5 min-w-0 text-foreground">
					<span className="w-2 h-2 shrink-0 rounded-full bg-primary" />
					<SeriesLabel name={planName} suffix=":" />
					<span data-value className="shrink-0 font-medium text-primary">
						{fmtEur(plan.value)}
					</span>
				</p>
			)}
			{saving && (
				<p data-series="saving" className="flex items-center gap-1.5 min-w-0 text-muted-foreground">
					<span className="w-2 h-2 shrink-0 rounded-full bg-muted-foreground/40" />
					<SeriesLabel name={savingName} suffix=":" />
					<span data-value className="shrink-0">
						{fmtEur(saving.value)}
					</span>
				</p>
			)}
		</div>
	);
};

interface NewPlanProjectionCardProps {
	/** Años hasta la jubilación (horizonte de la proyección). */
	years: number;
	/** Aportación mensual con la que se empieza el plan. */
	monthlyContribution: number;
	/** Rentabilidad del plan, decidida por comparsa (`/plan-returns/arca-plans`). */
	planReturn: PlanReturnView;
	planName: string;
	translate: (key: string) => string;
}

/**
 * Gráfica de proyección de un plan que arranca desde 0 € (plan nuevo, sin origen): compara
 * lo que se acumularía invirtiendo en el plan elegido frente a "solo ahorrar" el mismo
 * dinero sin invertir (rentabilidad 0). Mes a mes, con la tasa mensual y neta de comparsa:
 * ni se resta la comisión (ya está descontada) ni se suaviza el dato de 1 año (guía v3.1).
 * Componente controlado.
 */
const NewPlanProjectionCard: React.FC<NewPlanProjectionCardProps> = ({
	years,
	monthlyContribution,
	planReturn,
	planName,
	translate
}) => {
	const data = useMemo(
		() => projectComparisonByYear(0, monthlyContribution, rateOf(planReturn), 0, years),
		[monthlyContribution, planReturn, years]
	);

	const last = data[data.length - 1];
	const planFinal = last.plan;
	const savingFinal = last.bank;

	const savingName = translate('individual.newPlanProjection.noInvest') || 'Sin invertir';
	const arcaGeneric = translate('individual.transferProjection.arcaGeneric') || 'Plan de Arca';
	const planSeries = { full: planName, generic: arcaGeneric };
	const savingSeries = { full: savingName, generic: savingName };
	const fmtK = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`);
	const tickStep = Math.max(1, Math.round(years / 5));

	return (
		<div className="surface-card p-5 flex flex-col gap-4 h-full">
			<div>
				<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
					{(
						translate('individual.transferProjection.header') ||
						'Próximos {years} años hasta tu jubilación'
					).replace('{years}', String(years))}
				</p>
				<h3 className="text-lg font-bold mt-1">
					{translate('individual.newPlanProjection.title') || '¿Cuánto podrías acumular?'}
				</h3>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="surface-soft rounded-xl p-4">
					<p className="text-xs text-muted-foreground leading-tight">
						{(
							translate('individual.newPlanProjection.savingIn') || 'Solo ahorrando en {years} años'
						).replace('{years}', String(years))}
					</p>
					<p className="text-2xl font-bold font-heading mt-1">{fmtEur(savingFinal)}</p>
				</div>
				<div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
					<p className="text-xs text-muted-foreground leading-tight">
						{(translate('individual.transferProjection.arcaIn') || 'Con {plan} en {years} años')
							.replace('{plan}', planName)
							.replace('{years}', String(years))}
					</p>
					<p className="text-2xl font-bold font-heading mt-1 text-primary">{fmtEur(planFinal)}</p>
				</div>
			</div>

			<div className="flex-1 min-h-[240px]">
				<ResponsiveContainer width="100%" height="100%" minHeight={240}>
					<AreaChart data={data} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
						<defs>
							<linearGradient id="gradNewPlanProjection" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor="var(--primary)" stopOpacity={0.15} />
								<stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
							</linearGradient>
						</defs>
						<XAxis
							dataKey="year"
							tickFormatter={(v) => {
								if (v === 0) return translate('individual.dashboard.comparison.today') || 'Hoy';
								if (v % tickStep === 0) return `Año ${v}`;
								return '';
							}}
							tick={{ fontSize: 10 }}
							height={16}
							tickMargin={2}
						/>
						<YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} width={36} />
						<Tooltip content={<CustomTooltip planName={planSeries} savingName={savingSeries} />} />
						<Area
							type="monotone"
							dataKey="bank"
							stroke="var(--muted-foreground)"
							strokeWidth={1.5}
							strokeDasharray="5 3"
							fill="none"
						/>
						<Area
							type="monotone"
							dataKey="plan"
							stroke="var(--primary)"
							strokeWidth={2}
							fill="url(#gradNewPlanProjection)"
						/>
					</AreaChart>
				</ResponsiveContainer>
			</div>

			{/* Como la gráfica de traspaso: una línea por serie en escritorio; el nombre se recorta. */}
			<div className="flex flex-col gap-1.5 text-xs">
				<div
					data-testid="legend-plan"
					className="flex items-center gap-1.5 min-w-0 flex-wrap sm:flex-nowrap"
				>
					<div className="w-4 shrink-0 h-0.5 rounded bg-primary" />
					<span className="flex min-w-0 text-foreground">
						<SeriesLabel name={planSeries} title={planName} />
					</span>
					<span data-stats className="sm:shrink-0 text-muted-foreground">
						({legendStatsShort(translate, planReturn.managementFeePct, planReturn.annualPct)}
						{ageNote(translate, planReturn)})
					</span>
				</div>
				<div className="flex items-center gap-1.5 min-w-0">
					<div className="w-4 shrink-0 border-t-2 border-dashed border-muted-foreground/60" />
					<span className="text-foreground">{savingName}</span>
				</div>
			</div>

			<p className="text-xs text-center font-medium text-foreground/70">
				{(
					translate('individual.newPlanProjection.returnPeriod') ||
					'Rentabilidad neta anualizada a {period}.'
				).replace(
					'{period}',
					periodWithRange(
						translate,
						planReturn.periodYears,
						periodRangeLabel(translate, planReturn.period)
					)
				)}
			</p>

			<p className="text-xs text-muted-foreground text-center">
				{translate('individual.transferProjection.disclaimer') ||
					'Rentabilidades netas: el beneficio mostrado ya descuenta las comisiones de cada plan. Las rentabilidades pasadas no garantizan rentabilidades futuras.'}
			</p>
		</div>
	);
};

export default NewPlanProjectionCard;
